import { Icon, Text } from '@ui-component-library';
import { PlusCover } from './PlusTab';
import type { Section, Property } from './types';
import { formatAge } from '../../utils'
import { useDemoMinskogStore } from '../../store/DemoMinskogStore';
import { DEMO_PROMPTS } from '../../constants/demoPrompts';
import { FellingClassesLegend } from '../../helpers';

type DataItemProps = {
    label: string;
    value: string | number;
    hasPlus: boolean;
};

function DataItem({ label, value, hasPlus }: DataItemProps) {
    return (
        <PlusCover hasPlus={hasPlus}>
            <div className="w-full flex flex-col justify-between py-2 px-4 border border-solid border-[#E6E6E6] rounded-lg-alt">
                <Text variant="text-xs/leading-normal/normal" className="text-pebble-400">{label}</Text>
                <Text variant="text-sm/leading-normal/medium">{value}</Text>
            </div>
        </PlusCover>
    );
}

type DataTabProps = {
    section: Section;
    prop: Property;
    hasPlus: boolean;
    handleAndShowInfo: (name: string) => void;
    createAdFromSection: () => void;
    loadingCreateAd: boolean;
    setLoadingCreateAd: (loading: boolean) => void;
};

export function DataTab({
    section,
    prop,
    hasPlus,
    handleAndShowInfo,
    createAdFromSection,
    loadingCreateAd,
    setLoadingCreateAd,
}: DataTabProps) {
    const { DemoBlockFunctionWrapper } = useDemoMinskogStore()

    const isOwner = prop?.role ? prop.role === 'owner' : !prop?.is_shared;

    return (
        <div className="flex flex-wrap w-full p-6">
            {section.is_fucked && (
                <div className="flex w-full rounded-lg-alt border border-solid border-[#E6E6E6] items-start py-6 px-6 mb-6">
                    <Icon name="WarningCircle" className="fill-danger-500"></Icon>
                    <div className="ml-4">
                        <Text variant="text-sm/leading-normal/medium">Systemvarning</Text>
                        <Text variant="text-sm/leading-normal/normal">{section.is_fucked}</Text>
                    </div>
                </div>
            )}
            <div className="bg-cloud-200 rounded-lg w-full p-6 mb-6">
                <div className="flex">
                    <div className="rounded-lg border border-solid border-[#E6E6E6] min-w-11 h-11 flex items-center justify-center bg-white mr-2 px-2">
                        <Text variant="text-sm/leading-normal/medium">{section.localID || section.nr}</Text>
                    </div>
                    <div className="flex flex-col">
                        <Text variant="text-lg/leading-normal/medium">{section.volume} <Text variant="text-xs/leading-normal/normal">m³sk</Text></Text>
                        <Text variant="text-xs/leading-normal/normal" className="capitalize">{prop.designation.toLowerCase()}</Text>
                    </div>
                </div>
                <div className="h-px w-full bg-[#E6E6E6] my-6" />
                <PlusCover hasPlus={hasPlus}>
                    <div className="flex">
                        <div
                            className="flex rounded-lg-alt border border-solid border-[#E6E6E6] py-0-5 px-2 ms-2 items-center justify-center"
                            style={{ backgroundColor: FellingClassesLegend[section.felling_class as keyof typeof FellingClassesLegend]?.color ?? '#6b7280' }}
                        >
                            <Text variant="text-xs/leading-normal/medium" className="text-white-50">{section.felling_class}</Text>
                        </div>
                        <div className="flex rounded-lg-alt border border-solid border-[#E6E6E6] py-0-5 px-2 ml-2 items-center justify-center">
                            <Text variant="text-xs/leading-normal/medium">SI {section.major_tree}{section.si}</Text>
                        </div>
                        <div className="flex rounded-lg-alt border border-solid border-[#E6E6E6] py-0-5 px-2 ml-2 items-center justify-center">
                            <Text variant="text-xs/leading-normal/medium">Ålder {formatAge(section.age)}</Text>
                        </div>
                        <div className="flex rounded-lg-alt border border-solid border-[#E6E6E6] py-0-5 px-2 ml-2 items-center justify-center">
                            <Text variant="text-xs/leading-normal/medium">Area {Number(section.area).toLocaleString()}</Text>
                        </div>
                    </div>
                    <div className="h-px w-full bg-[#E6E6E6] my-6" />
                    <div className="flex items-center">
                        <div className="flex justify-between me-1-5">
                            <div className="flex items-center justify-center mr-2">
                                <div className="w-3 h-3 rounded-full bg-[#D5EE64] mr-1" />
                                <Text variant="text-xs/leading-normal/medium">T{section.tall}</Text>
                            </div>
                            <div className="flex items-center justify-center mr-2">
                                <div className="w-3 h-3 rounded-full bg-[#3F8888] mr-1" />
                                <Text variant="text-xs/leading-normal/medium">G{section.gran}</Text>
                            </div>
                            <div className="flex items-center justify-center">
                                <div className="w-3 h-3 rounded-full bg-[#7C6ED9] mr-1" />
                                <Text variant="text-xs/leading-normal/medium">L{section.lov}</Text>
                            </div>
                        </div>
                        <div className="w-full flex rounded-xs h-2 flex overflow-hidden ml-[7.5px]">
                            <div style={{ width: `${section.tall}%`, backgroundColor: "#D5EE64" }}></div>
                            <div style={{ width: `${section.gran}%`, backgroundColor: "#3F8888" }}></div>
                            <div style={{ width: `${section.lov}%`, backgroundColor: "#7C6ED9" }}></div>
                        </div>
                    </div>
                </PlusCover>
            </div>
            
            <div className="flex flex-wrap w-full rounded-lg-alt">
                <Text variant="text-sm/leading-normal/medium" className="py-2 px-6">Genvägar</Text>
                {isOwner && (
                    <div className="w-full py-2 px-4 border border-solid border-[#E6E6E6] rounded-lg-alt mt-2 cursor-pointer" onClick={() => { setLoadingCreateAd(true); createAdFromSection(); }}>
                        <div className="flex items-center gap-3">
                            {!loadingCreateAd ? <Icon name="CoinsSimple" size="large"></Icon> : <div className="h-8 w-8 animate-spin rounded-full border-2 border-solid border-charcoal-500 border-t-transparent" />}
                            <div className="flex flex-col">
                                <Text variant="text-sm/leading-normal/medium">Dags att avverka?</Text>
                                <Text variant="text-xs/leading-normal/normal">Ta in anbud på denna avdelning</Text>
                            </div>
                            <Icon name="ChevronDownSimple" className="-rotate-90 fill-[#000000] ml-auto" size="large" />
                        </div>
                    </div>
                )}
                <PlusCover hasPlus={hasPlus}>
                    <div className="w-full py-2 px-4 border border-solid border-[#E6E6E6] rounded-lg-alt mt-2 cursor-pointer" onClick={() => DemoBlockFunctionWrapper(() => {window.location.href = "/redirect-book-meeting/"; }, DEMO_PROMPTS.BOOK_MEETING)}>
                        <div className="flex items-center gap-3">
                            <Icon name="CustomerSupportSimple" size="large"/>
                            <div className="flex flex-col">
                                <Text variant="text-sm/leading-normal/medium">Behöver du hjälp?</Text>
                                <Text variant="text-xs/leading-normal/normal">Få hjälp av våra rådgivare</Text>
                            </div>
                            <Icon name="ChevronDownSimple" className="-rotate-90 fill-[#000000] ml-auto" size="large" />
                        </div>
                    </div>
                </PlusCover>
            </div>
            <div className="flex flex-wrap w-full rounded-lg-alt">
                <div className="flex justify-start items-center w-full">
                    <Text variant="text-sm/leading-normal/medium" className="py-2 pl-6 pr-2">Översiktlig data </Text>
                    <div onClick={() => { handleAndShowInfo("data_information"); }} className="cursor-pointer">
                        <Icon name="InfoOutlined" size="medium" className="fill-charcoal-500" />
                    </div>
                </div>

                <div className="flex w-full flex-wrap gap-2">
                    <div className="flex flex-grow flex-col gap-2">
                        <DataItem
                            label="Avd. nr"
                            value={`${section.localID}`}
                            hasPlus={true}
                        />
                        <DataItem
                            label="Volym"
                            value={`${section.volume} m³sk`}
                            hasPlus={true}
                        />
                        <DataItem
                            label="Tall"
                            value={`${Math.round(section.tall * section.volume / 100)} m³sk`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Gran"
                            value={`${Math.round(section.gran * section.volume / 100)} m³sk`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Löv"
                            value={`${Math.round(section.lov * section.volume / 100)} m³sk`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Ståndordsindex"
                            value={`${section.major_tree}${section.si}`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Medeldiameter"
                            value={`${section.diameter} cm`}
                            hasPlus={hasPlus}
                        />
                    </div>
                    <div className="flex flex-grow flex-col gap-2">
                        <DataItem
                            label="Area"
                            value={`${Number(section.area).toLocaleString()} ha`}
                            hasPlus={true}
                        />
                        <DataItem
                            label="TGL"
                            value={`${section.tall} | ${section.gran} | ${section.lov}`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Grundyta"
                            value={`${section.grun} m²/ha`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Ålder"
                            value={`${formatAge(section.age)} år`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Medelhöjd"
                            value={`${section.height} m`}
                            hasPlus={hasPlus}
                        />
                        <DataItem
                            label="Skannat år"
                            value={`${section.scanned_year}`}
                            hasPlus={true}
                        />
                        <DataItem
                            label="Framräknat till"
                            value={new Date().getFullYear()}
                            hasPlus={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
